import { MainContainer, Line } from './styles';
import { ColumnProps, DataTable } from 'components/Table';
import { useEffect, useState } from 'react';
import { PaginationProps } from 'interfaces/IPagination';
import { Box } from '@mui/material';
import { PageContainer } from 'components/PageContainer';
import { CustomPagination } from 'components/CustomPagination';
import { Popup } from 'components/Popup';
import { Toolbar } from 'components/Toolbar';
import { EditButton } from 'components/EditButton';
import { IPhysicalTransship } from 'interfaces/IPhysicalTransship';
import { physicalTransshipService } from 'services/physicalTransshipService';
import { transshipPointsService } from 'services/transshipPointsService';
import {
  PhysicalTransshipForm,
  IPhysicalTransshipSubmitValues,
} from './components/PhysicalTransshipForm';
import { toast } from 'react-toastify';

const columns: ColumnProps[] = [
  { name: 'Nome', align: 'center' },
  { name: 'Sigla', align: 'center' },
  { name: 'Ativo', align: 'center' },
  { name: 'Observações', align: 'center' },
  { name: '', align: 'center' },
];

export function PhysicalTransshipPage() {
  const [dataTable, setDataTable] = useState<PaginationProps<IPhysicalTransship>>(
    {} as PaginationProps<IPhysicalTransship>
  );
  const [formData, setFormData] = useState<Partial<IPhysicalTransship>>({});
  const [linkedAgileDepotIds, setLinkedAgileDepotIds] = useState<number[]>([]);
  const [isPost, setIsPost] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [params, setParams] = useState({ page: 1, pageSize: 15, search: '' });
  const [loading, setLoading] = useState(false);

  const fetchData = () => {
    setLoading(true);
    physicalTransshipService
      .getList({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search || undefined,
        orderBy: '-id',
      })
      .then((response) => setDataTable(response))
      .finally(() => setLoading(false));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setParams((state) => ({ ...state, page: 1, pageSize }));
  };

  const handleAdd = () => {
    setIsPost(true);
    setFormData({ inUse: true });
    setLinkedAgileDepotIds([]);
    setPopupVisible(true);
  };

  const handleEdit = (row: IPhysicalTransship) => {
    setIsPost(false);
    setFormData(row);
    transshipPointsService
      .getTransshipPoints({
        physicalTransshipPointId: row.id,
        pageSize: 500,
        page: 1,
        orderBy: 'name',
      })
      .then((res) => {
        setLinkedAgileDepotIds(res.results?.map((r) => r.id) ?? []);
        setPopupVisible(true);
      })
      .catch(() => {
        toast.error('Não foi possível carregar os depots Agile vinculados.');
      });
  };

  const handleSave = async (values: IPhysicalTransshipSubmitValues) => {
    if (isPost) {
      const created = await physicalTransshipService.create(values);
      if (created !== undefined) {
        setPopupVisible(false);
        fetchData();
      }
    } else {
      const updated = await physicalTransshipService.update(values);
      if (updated !== undefined) {
        setPopupVisible(false);
        fetchData();
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [params.page, params.pageSize, params.search]);

  return (
    <>
      <Popup
        popupTitle={isPost ? 'Novo transbordo' : 'Editar transbordo'}
        open={popupVisible}
        expand
        setOpen={setPopupVisible}
        content={
          <PhysicalTransshipForm
            key={isPost ? 'new' : formData.id ?? 'edit'}
            data={formData}
            isNew={isPost}
            initialAgileDepotIds={linkedAgileDepotIds}
            onSubmit={handleSave}
          />
        }
      />
      <div className="pageContent">
        <PageContainer>
          <MainContainer>
            <div className="infos">
              <h3>Transbordos</h3>
              <Toolbar
                params={params}
                paramState={setParams}
                loadingState={loading}
                placeholder="Buscar por nome ou sigla"
                alignButtonsToEnd={true}
                add={true}
                addDisabled={loading}
                onAdd={handleAdd}
                page="PhysicalTransship"
                hideFilter
              />
            </div>
            <DataTable
              loading={loading}
              columns={columns}
              contentHeight="40vh"
              loadingType="inline"
            >
              {(dataTable?.results ?? []).map((row) => (
                <Line className="bodyLine" key={row.id}>
                  <td className="contentTable">{row.name}</td>
                  <td className="contentTable">{row.initials || '—'}</td>
                  <td className="contentTable">{row.inUse ? 'Sim' : 'Não'}</td>
                  <td
                    className="contentTable"
                    style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={row.notes || undefined}
                  >
                    {row.notes || '—'}
                  </td>
                  <td align="center" className="contentTable contentEdit">
                    <EditButton onClick={() => handleEdit(row)} />
                  </td>
                </Line>
              ))}
            </DataTable>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
              <CustomPagination
                currentPage={params.page}
                totalPages={dataTable?.totalPages ?? 1}
                onPageChange={handlePageChange}
                pageSize={params.pageSize}
                onPageSizeChange={handlePageSizeChange}
                showPageSizeSelector
              />
            </Box>
            <div className="spacer" />
          </MainContainer>
        </PageContainer>
      </div>
    </>
  );
}
